import React from 'react'
import PropTypes from 'prop-types'
import { WingsButton } from '../buttons'
import './WingsSearchBar.css'

/**
 * WingsSearchBar Component
 *
 * A search input bar with an integrated teal search button.
 * Accepts a placeholder, value, onChange handler, and onSubmit callback.
 *
 * @component
 * @example
 * // Uncontrolled with submit callback
 * <WingsSearchBar
 *   placeholder="Search by name, job, location, company"
 *   onSubmit={(value) => console.log(value)}
 * />
 *
 * @example
 * // Controlled
 * const [query, setQuery] = useState('')
 * <WingsSearchBar
 *   value={query}
 *   onChange={setQuery}
 *   onSubmit={(value) => handleSearch(value)}
 * />
 */
const WingsSearchBar = ({
  placeholder = '',
  value,
  onChange,
  onSubmit,
  disabled = false,
  className = '',
  ...props
}) => {
  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit?.(value ?? '')
  }

  const handleChange = (e) => {
    onChange?.(e.target.value)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onSubmit?.(value ?? e.target.value)
    }
  }

  const containerClasses = [
    'wings-search-bar',
    disabled ? 'wings-search-bar--disabled' : '',
    className
  ].filter(Boolean).join(' ')

  return (
    <form
      className={containerClasses}
      onSubmit={handleSubmit}
      role="search"
      {...props}
    >
      <input
        className="wings-search-bar-input"
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label={placeholder}
      />
      <WingsButton
        className="wings-search-bar-button"
        type="submit"
        disabled={disabled}
        aria-label="Search"
        icon={true}
        iconName="search"
        size="small"
      />
    </form>
  )
}

WingsSearchBar.propTypes = {
  /** Placeholder text displayed inside the input */
  placeholder: PropTypes.string,
  /** Controlled value of the input */
  value: PropTypes.string,
  /** Callback fired when the input value changes; receives the new string value */
  onChange: PropTypes.func,
  /** Callback fired on form submit or Enter key; receives the current input value */
  onSubmit: PropTypes.func,
  /** Disables both the input and the button */
  disabled: PropTypes.bool,
  /** Additional class names to apply to the root element */
  className: PropTypes.string
}

export default WingsSearchBar
